<template>
    <Head :title="t('connections.calendarImport.pageTitle')" />
    <BaseLayout>
        <template #header>
            <BaseLink type="button" class="flex gap-2 items-center text-ink-muted pt-4 w-fit" :href="connections_page_url">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i> <span class="body-small">{{ t("connections.calendarImport.backLink") }}</span>
            </BaseLink>
        </template>
        <BasePageHeader :title="t('connections.calendarImport.title')" :subtitle="t('connections.calendarImport.subtitle')">
            <template #eyebrow>
                <div class="w-12 h-12 rounded-xl bg-surface-sunken flex items-center justify-center">
                    <i class="bi bi-calendar3 text-xl text-ink-muted" aria-hidden="true"></i>
                </div>
            </template>
        </BasePageHeader>

        <div v-if="!isLoading && people.length > 0" class="flex flex-wrap gap-4 items-center">
            <div class="relative max-w-sm grow">
                <div class="absolute top-1/2 -translate-y-1/2 left-0 pl-3 pointer-events-none">
                    <i class="bi bi-search text-control-text" aria-hidden="true"></i>
                </div>
                <input
                    v-model="searchQuery"
                    type="text"
                    :aria-label="t('connections.calendarImport.search.label')"
                    :placeholder="t('connections.calendarImport.search.label')"
                    class="body-small focus-field rounded-lg p-2 pl-10 border border-control-border bg-control-bg text-control-text-strong w-full" />
            </div>
            <BaseSelect v-model="sortOption" :aria-label="t('connections.calendarImport.sort.label')" :options="sortOptions" trigger-class="h-[38px]! px-3! rounded-lg! w-44!" />
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-10">
            <LoadingSpinner class="w-8 h-8" />
        </div>

        <template v-else-if="people.length > 0">
            <template v-if="filteredAndSortedPeople.length > 0">
                <div class="flex items-center justify-between gap-4">
                    <BaseButton theme="tertiary" size="sm" @click="handleSelectAll">
                        {{ allVisibleSelected ? t("connections.calendarImport.deselectAll") : t("connections.calendarImport.selectAll") }}
                    </BaseButton>
                    <span class="text-ink-muted body-small">{{ t("connections.calendarImport.selectedCount", { selected: selectedCount, total: people.length }) }}</span>
                </div>

                <div class="flex flex-col gap-3">
                    <CalendarPersonCard
                        v-for="person in visiblePeople"
                        :key="person.email"
                        :person="person"
                        :selected="selectedEmails.has(person.email)"
                        @toggle="handleToggle(person)" />

                    <BaseButton v-if="hasMorePeople && !showAll" theme="neutral" class="w-full" @click="showAll = true">
                        {{ t("connections.calendarImport.showMore", { count: hiddenCount }, hiddenCount) }}
                    </BaseButton>
                </div>
            </template>

            <div v-else class="text-center py-10">
                <p class="body-small text-ink-muted">{{ t("connections.calendarImport.noSearchResults") }}</p>
                <BaseButton theme="tertiary" size="sm" class="mt-2" @click="searchQuery = ''">{{ t("connections.calendarImport.clearSearch") }}</BaseButton>
            </div>
        </template>

        <div v-else class="text-center py-10">
            <p class="body-small text-ink-muted">{{ t("connections.calendarImport.noPeopleFound") }}</p>
            <p class="body-small text-ink-muted mt-2">{{ t("connections.calendarImport.everyoneAdded") }}</p>
        </div>

        <div class="flex items-center justify-between border-t border-border pt-6 mt-4">
            <BaseButton theme="tertiary" class="body-small" @click="handleCancel">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" class="body-small min-w-[160px] justify-center" :disabled="selectedCount === 0 || isSubmitting" @click="handleSubmit">
                <LoadingSpinner v-if="isSubmitting" class="w-4 h-4" />
                <template v-else>
                    <i class="bi bi-check-lg" aria-hidden="true"></i>
                    {{ t("connections.calendarImport.addSelected", { count: selectedCount }, selectedCount) }}
                </template>
            </BaseButton>
        </div>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import CalendarPersonCard from "~vue/Connections/CalendarPersonCard.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { BulkConnectionResponse, CalendarPeopleResponse, CalendarPerson } from "~vue/types/connection";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { connections_page_url, calendar_people_api_url, bulk_connection_api_url } = defineProps<{
    connections_page_url: string;
    calendar_people_api_url: string;
    bulk_connection_api_url: string;
    public_profile_enabled: boolean;
    has_calendar_integration: boolean;
    event_context: Record<string, string>;
}>();

const INITIAL_DISPLAY_COUNT = 10;

const { t } = useI18n();

const sortOptions = computed(() => [
    { key: "meetings", label: t("connections.calendarImport.sort.mostMeetings") },
    { key: "name", label: t("connections.calendarImport.sort.nameAsc") },
]);

const people = ref<CalendarPerson[]>([]);
const selectedEmails = ref<Set<string>>(new Set());
const isLoading = ref(true);
const isSubmitting = ref(false);
const showAll = ref(false);
const searchQuery = ref("");
const sortOption = ref("meetings");

const filteredAndSortedPeople = computed(() => {
    let result = [...people.value];

    // Apply search filter
    const query = searchQuery.value.toLowerCase().trim();
    if (query) {
        result = result.filter((p) => p.name.toLowerCase().includes(query) || p.email.toLowerCase().includes(query));
    }

    // Apply sorting
    if (sortOption.value === "name") {
        result.sort((a, b) => a.name.localeCompare(b.name));
    } else {
        // Default: sort by meeting count descending
        result.sort((a, b) => b.meeting_count - a.meeting_count);
    }

    return result;
});

const selectedCount = computed(() => selectedEmails.value.size);
const visiblePeople = computed(() => (showAll.value ? filteredAndSortedPeople.value : filteredAndSortedPeople.value.slice(0, INITIAL_DISPLAY_COUNT)));
const hasMorePeople = computed(() => filteredAndSortedPeople.value.length > INITIAL_DISPLAY_COUNT);
const hiddenCount = computed(() => Math.max(0, filteredAndSortedPeople.value.length - INITIAL_DISPLAY_COUNT));
const allVisibleSelected = computed(() => visiblePeople.value.length > 0 && visiblePeople.value.every((p) => selectedEmails.value.has(p.email)));

const selectedPeople = computed(() => people.value.filter((p) => selectedEmails.value.has(p.email)));

// Reset pagination when search or sort changes
watch([searchQuery, sortOption], () => {
    showAll.value = false;
});

const { data, execute: fetchPeople, onFetchResponse: onPeopleResponse } = useFetch(calendar_people_api_url, { immediate: false }).get().json<CalendarPeopleResponse>();

onPeopleResponse(() => {
    if (data.value?.people) {
        people.value = data.value.people;

        const peopleWithProfiles = people.value.filter((p) => p.has_public_profile).length;
        logUserInteraction("calendar_import_page_viewed", {
            people_count: people.value.length,
            people_with_profiles: peopleWithProfiles,
        });
    }
    isLoading.value = false;
});

// Prime CalendarIntegrationEvents before fetching people — same call the homepage makes
const { onFetchResponse: onSyncResponse, onFetchError: onSyncError } = useFetch("/api/calendar_integration/events/").get().json();
onSyncResponse(() => fetchPeople());
onSyncError(() => fetchPeople());

const handleSelectAll = () => {
    if (allVisibleSelected.value) {
        // Deselect all visible people
        const newSelected = new Set(selectedEmails.value);
        visiblePeople.value.forEach((p) => newSelected.delete(p.email));
        selectedEmails.value = newSelected;
    } else {
        // Select all visible people
        const newSelected = new Set(selectedEmails.value);
        visiblePeople.value.forEach((p) => newSelected.add(p.email));
        selectedEmails.value = newSelected;
        logUserInteraction("calendar_import_select_all_clicked", { people_count: visiblePeople.value.length });
    }
};

const handleToggle = (person: CalendarPerson) => {
    const newSelected = new Set(selectedEmails.value);
    const isCurrentlySelected = newSelected.has(person.email);

    if (isCurrentlySelected) {
        newSelected.delete(person.email);
    } else {
        newSelected.add(person.email);
    }
    selectedEmails.value = newSelected;

    logUserInteraction("calendar_import_person_toggled", {
        email: person.email,
        selected: !isCurrentlySelected,
        has_public_profile: person.has_public_profile,
    });
};

const handleCancel = () => {
    logUserInteraction("calendar_import_cancelled", { selected_count: selectedCount.value });
    router.visit(connections_page_url);
};

const payload = computed(() => ({
    source: "calendar_import",
    people: selectedPeople.value.map((person) => ({
        email: person.email,
        name: person.name,
        title: person.title || "",
        public_profile_user_id: person.public_profile_user_id,
    })),
}));

const {
    execute: submitImport,
    onFetchResponse: onSubmitResponse,
    onFetchError,
    data: submitData,
} = useFetch(bulk_connection_api_url, { immediate: false }).post(payload).json<BulkConnectionResponse>();

onSubmitResponse(() => {
    const profilesLinked = submitData.value?.profiles_linked || 0;
    const createdCount = submitData.value?.connections?.length || 0;

    logUserInteraction("calendar_import_success", {
        created_count: createdCount,
        profiles_auto_linked: profilesLinked,
    });

    // Redirect to connections page with success message
    router.visit(`${connections_page_url}?imported=${createdCount}&linked=${profilesLinked}`);
});

onFetchError(() => {
    isSubmitting.value = false;
});

const handleSubmit = async () => {
    if (selectedCount.value === 0) {
        return;
    }

    const profilesLinked = selectedPeople.value.filter((p) => p.has_public_profile).length;
    logUserInteraction("calendar_import_submitted", {
        selected_count: selectedCount.value,
        profiles_linked: profilesLinked,
    });

    isSubmitting.value = true;
    await submitImport();
};
</script>
